<template>
  <div class="col-sm-12">
    <div class="card">
      <div class="card-header pb-0">
        <h4>Lista de Usuarios</h4>
        <span>Consulta y gestión de usuarios del sistema</span>
      </div>
      <div class="card-body">
        <div class="mb-3">
          <div class="row g-3 align-items-end">
            <div class="col-12 col-md-4 col-lg-3">
              <label for="table-complete-search" class="form-label">Buscar</label>
              <input
                id="table-complete-search"
                type="text"
                class="form-control"
                v-model="filterQuery"
                placeholder="ID, nombre, email..."
              />
            </div>
            <div class="col-12 col-md-4 col-lg-3">
              <label for="filter-role" class="form-label">Rol</label>
              <select
                id="filter-role"
                class="form-select"
                v-model="filterRole"
                :disabled="isTransportista || !roleOptions.length"
              >
                <option value="">Todos</option>
                <option v-for="role in roleOptions" :key="role" :value="role">
                  {{ role }}
                </option>
              </select>
            </div>
            <div class="col-12 col-md-4 col-lg-3">
              <label for="filter-status" class="form-label">Estado</label>
              <select
                id="filter-status"
                class="form-select"
                v-model="filterStatus"
                :disabled="!statusOptions.length"
              >
                <option value="">Todos</option>
                <option v-for="status in statusOptions" :key="status.value" :value="status.value">
                  {{ status.label }}
                </option>
              </select>
            </div>
            <div class="col-12 col-md-4 col-lg-3">
              <label for="filter-store" class="form-label">Sucursal</label>
              <select
                id="filter-store"
                class="form-select"
                v-model="filterStore"
                :disabled="loadingStores || !storeOptions.length"
              >
                <option value="">Todas</option>
                <option
                  v-for="store in storeOptions"
                  :key="store.id || store.division || store.sucursal"
                  :value="String(store.division ?? store.id ?? '')"
                >
                  {{ store.sucursal || `División ${store.division ?? store.id ?? 'N/A'}` }}
                </option>
              </select>
            </div>
            <div v-if="canManageUsers" class="col-12 col-md-4 col-lg-3">
              <router-link to="/users/alta" class="btn btn-primary">
                <vue-feather type="user-plus" size="16" class="me-2"></vue-feather>
                Alta de usuario
              </router-link>
            </div>
          </div>
        </div>

        <!-- Vista de tabla para desktop -->
        <div class="table-responsive theme-scrollbar data-table d-none d-lg-block">
          <form>
            <table class="table table-striped table-bordered">
              <thead>
                <tr>
                  <th scope="col">Nombre</th>
                  <th scope="col">Email</th>
                  <th scope="col">Sucursal por Defecto</th>
                  <th scope="col">Rol</th>
                  <th scope="col">Estado</th>
                  <th scope="col">Creado</th>
                  <th scope="col">Creado por</th>
                  <th scope="col">Acciones</th>
                </tr>
              </thead>
              <tbody v-if="!get_rows.length">
                <tr class="odd">
                  <td valign="top" colspan="8" class="dataTables_empty">
                    <div v-if="loading" class="text-center">
                      <div class="spinner-border text-primary" role="status">
                        <span class="sr-only">Cargando...</span>
                      </div>
                      <p class="mt-2">Cargando usuarios...</p>
                    </div>
                    <div v-else>No se encontraron usuarios</div>
                  </td>
                </tr>
              </tbody>
              <tbody v-if="get_rows.length">
                <tr v-for="row in get_rows" :key="row.uid">
                  <td scope="row">{{ row.name || 'N/A' }}</td>
                  <td>{{ row.email || 'N/A' }}</td>
                  <td>{{ getStoreName(row.defaultStore) || 'N/A' }}</td>
                  <td>
                    <span class="badge badge-primary">{{ row.rol || 'N/A' }}</span>
                  </td>
                  <td>
                    <span class="badge" :class="getStatusBadgeClass(row.status)">
                      {{ row.status || 'N/A' }}
                    </span>
                  </td>
                  <td>{{ formatDate(row.createdAt) }}</td>
                  <td>{{ formatActor(row.createdBy) }}</td>
                  <td>
                    <div class="action-buttons">
                      <!-- Copiar ID - Solo Administrador y Soporte -->
                      <button
                        v-if="canCopyUserId"
                        type="button"
                        class="btn btn-sm btn-info me-1"
                        @click="copyUserId(row.uid)"
                        title="Copiar ID de usuario"
                      >
                        <vue-feather type="copy" size="14"></vue-feather>
                      </button>

                      <!-- Reenviar correo de restablecimiento - Roles autorizados -->
                      <button
                        v-if="canManageUsers"
                        type="button"
                        class="btn btn-sm btn-secondary me-1"
                        @click="$emit('resetPassword', row)"
                        title="Reenviar correo de restablecimiento de contraseña"
                      >
                        <vue-feather type="mail" size="14"></vue-feather>
                      </button>

                      <!-- Editar - Roles autorizados -->
                      <button
                        v-if="canManageUsers"
                        type="button"
                        class="btn btn-sm btn-primary me-1"
                        @click="$emit('edit', row)"
                        title="Editar usuario"
                      >
                        <vue-feather type="edit" size="14"></vue-feather>
                      </button>

                      <!-- Eliminar - Solo Administrador -->
                      <button
                        v-if="canDelete"
                        type="button"
                        class="btn btn-sm btn-danger"
                        @click="$emit('delete', row)"
                        title="Eliminar usuario"
                      >
                        <vue-feather type="trash-2" size="14"></vue-feather>
                      </button>

                      <!-- Mensaje si no hay permisos -->
                      <span v-if="!canManageUsers && !canDelete" class="text-muted small">
                        Sin permisos
                      </span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </form>
        </div>

        <!-- Vista de cards para tablets y móviles -->
        <div class="d-lg-none">
          <!-- Estado vacío o loading -->
          <div v-if="!get_rows.length" class="text-center py-5">
            <div v-if="loading">
              <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Cargando...</span>
              </div>
              <p class="mt-2">Cargando usuarios...</p>
            </div>
            <div v-else class="text-muted">No se encontraron usuarios</div>
          </div>

          <!-- Cards de usuarios -->
          <div v-else class="row g-3">
            <div v-for="row in get_rows" :key="row.uid" class="col-12 col-md-6">
              <div class="card user-card h-100">
                <div class="card-body">
                  <!-- Header del card con nombre y estado -->
                  <div class="d-flex justify-content-between align-items-start mb-3">
                    <div class="flex-grow-1">
                      <h5 class="card-title mb-1">{{ row.name || 'N/A' }}</h5>
                      <small class="text-muted">ID: {{ row.uid?.substring(0, 8) }}...</small>
                    </div>
                    <span class="badge" :class="getStatusBadgeClass(row.status)">
                      {{ row.status || 'N/A' }}
                    </span>
                  </div>

                  <!-- Información del usuario -->
                  <div class="user-info">
                    <div class="info-item mb-2">
                      <div class="info-label">
                        <vue-feather type="mail" size="14"></vue-feather>
                        <span>Email</span>
                      </div>
                      <div class="info-value">{{ row.email || 'N/A' }}</div>
                    </div>

                    <div class="info-item mb-2">
                      <div class="info-label">
                        <vue-feather type="briefcase" size="14"></vue-feather>
                        <span>Rol</span>
                      </div>
                      <div class="info-value">
                        <span class="badge badge-primary">{{ row.rol || 'N/A' }}</span>
                      </div>
                    </div>

                    <div class="info-item mb-3">
                      <div class="info-label">
                        <vue-feather type="map-pin" size="14"></vue-feather>
                        <span>Sucursal por Defecto</span>
                      </div>
                      <div class="info-value">{{ getStoreName(row.defaultStore) || 'N/A' }}</div>
                    </div>
                  </div>

                  <!-- Acciones -->
                  <div class="card-actions">
                    <div class="d-flex flex-wrap gap-2">
                      <!-- Copiar ID - Solo Administrador y Soporte -->
                      <button
                        v-if="canCopyUserId"
                        type="button"
                        class="btn btn-sm btn-info"
                        @click="copyUserId(row.uid)"
                        title="Copiar ID de usuario"
                      >
                        <vue-feather type="copy" size="14"></vue-feather>
                        <span class="d-md-none ms-1">ID</span>
                      </button>

                      <!-- Reenviar correo de restablecimiento - Roles autorizados -->
                      <button
                        v-if="canManageUsers"
                        type="button"
                        class="btn btn-sm btn-secondary"
                        @click="$emit('resetPassword', row)"
                        title="Reenviar correo de restablecimiento de contraseña"
                      >
                        <vue-feather type="mail" size="14"></vue-feather>
                        <span class="d-md-none ms-1">Correo</span>
                      </button>

                      <!-- Editar - Roles autorizados -->
                      <button
                        v-if="canManageUsers"
                        type="button"
                        class="btn btn-sm btn-primary"
                        @click="$emit('edit', row)"
                        title="Editar usuario"
                      >
                        <vue-feather type="edit" size="14"></vue-feather>
                        <span class="d-md-none ms-1">Editar</span>
                      </button>

                      <!-- Eliminar - Solo Administrador -->
                      <button
                        v-if="canDelete"
                        type="button"
                        class="btn btn-sm btn-danger"
                        @click="$emit('delete', row)"
                        title="Eliminar usuario"
                      >
                        <vue-feather type="trash-2" size="14"></vue-feather>
                        <span class="d-md-none ms-1">Eliminar</span>
                      </button>

                      <!-- Mensaje si no hay permisos -->
                      <span
                        v-if="!canManageUsers && !canDelete"
                        class="text-muted small align-self-center"
                      >
                        Sin permisos
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <ul class="pagination m-3 justify-content-end pagination-primary" v-if="get_rows.length">
          <li class="page-item">
            <a class="page-link" aria-label="Previous" @click="prev()">
              <span aria-hidden="true">&laquo;</span>
            </a>
          </li>
          <li
            class="page-item"
            v-for="i in num_pages()"
            :key="i"
            v-bind:class="[i == currentPage ? 'active' : '']"
            v-on:click="change_page(i)"
          >
            <a class="page-link">{{ i }}</a>
          </li>
          <li class="page-item">
            <a class="page-link" aria-label="Next" @click="change()">
              <span aria-hidden="true">&raquo;</span>
            </a>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { toast } from 'vue3-toastify'
import { getAllStores } from '@/services/usersService'
import { isHighImpactDeletionBlocked } from '@/utils/highImpactDeletionGuard'
import { formatUserActor } from '@/utils/userAudit'

export default {
  name: 'UsersTable',
  props: {
    users: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      elementsPerPage: 10,
      currentPage: 1,
      filterQuery: '',
      filterRole: '',
      filterStatus: '',
      filterStore: '',
      filteredData: [],
      stores: [],
      loadingStores: false,
    }
  },
  computed: {
    ...mapGetters('auth', ['userRole', 'currentUser']),
    canManageUsers() {
      // Administrador, Soporte y Servicio a Domicilio pueden gestionar usuarios (Transportista no)
      const role = this.userRole || (this.currentUser && this.currentUser.rol)
      return role === 'Administrador' || role === 'Soporte' || role === 'Servicio a Domicilio'
    },
    canCopyUserId() {
      // Solo Administrador y Soporte pueden copiar el ID
      const role = this.userRole || (this.currentUser && this.currentUser.rol)
      return role === 'Administrador' || role === 'Soporte'
    },
    canDelete() {
      if (isHighImpactDeletionBlocked()) return false
      // Solo Administrador puede eliminar
      const role = this.userRole || (this.currentUser && this.currentUser.rol)
      return role === 'Administrador'
    },
    isTransportista() {
      const role = this.userRole || (this.currentUser && this.currentUser.rol)
      return role === 'Transportista'
    },
    roleOptions() {
      if (!Array.isArray(this.users)) return []
      const roles = new Set()
      this.users.forEach(user => {
        if (user && user.rol) {
          roles.add(String(user.rol))
        }
      })
      const options = Array.from(roles).sort((a, b) => a.localeCompare(b))
      if (this.isTransportista && !options.includes('Transportista')) {
        options.unshift('Transportista')
      }
      return options
    },
    statusOptions() {
      if (!Array.isArray(this.users)) return []
      const statuses = new Set()
      this.users.forEach(user => {
        if (user && user.status !== undefined && user.status !== null) {
          statuses.add(String(user.status).toLowerCase())
        }
      })
      return Array.from(statuses)
        .sort((a, b) => a.localeCompare(b))
        .map(value => ({
          value,
          label: value.charAt(0).toUpperCase() + value.slice(1),
        }))
    },
    storeOptions() {
      if (!Array.isArray(this.stores)) return []
      return this.stores
        .filter(store => store && (store.sucursal || store.division || store.id))
        .map(store => ({
          ...store,
          sucursal: store.sucursal || '',
        }))
        .sort((a, b) => {
          const nameA = a.sucursal || String(a.division ?? a.id ?? '')
          const nameB = b.sucursal || String(b.division ?? b.id ?? '')
          return nameA.localeCompare(nameB)
        })
    },
    get_rows() {
      const start = (this.currentPage - 1) * this.elementsPerPage
      const end = start + this.elementsPerPage
      return this.filteredData.slice(start, end)
    },
  },
  watch: {
    users: {
      handler() {
        this.applyFilters()
      },
      immediate: true,
    },
    filterQuery() {
      this.applyFilters()
    },
    filterRole() {
      if (this.isTransportista && this.filterRole !== 'Transportista') {
        this.filterRole = 'Transportista'
        return
      }
      this.applyFilters()
    },
    filterStatus() {
      this.applyFilters()
    },
    filterStore() {
      this.applyFilters()
    },
  },
  async created() {
    if (this.isTransportista) {
      this.filterRole = 'Transportista'
    }
    await this.loadStores()
  },
  methods: {
    applyFilters() {
      const dataset = Array.isArray(this.users) ? this.users : []
      const searchLower = this.filterQuery ? this.filterQuery.trim().toLowerCase() : ''
      const selectedRole = this.filterRole
      const selectedStatus = this.filterStatus
      const selectedStore = this.filterStore

      const filtered = dataset.filter(row => {
        if (!row || typeof row !== 'object') return false

        const defaultStoreValue =
          row.defaultStore !== undefined && row.defaultStore !== null
            ? String(row.defaultStore)
            : ''
        const storeName = this.getStoreName(row.defaultStore) || ''

        const searchFields = [
          row.uid,
          row.name,
          row.email,
          row.rol,
          row.status,
          defaultStoreValue,
          storeName,
        ]

        const matchesSearch =
          !searchLower ||
          searchFields.some(field => {
            if (!field && field !== 0) return false
            return String(field).toLowerCase().includes(searchLower)
          })

        const matchesRole = !selectedRole || String(row.rol || '') === selectedRole
        const matchesStatus =
          !selectedStatus || String(row.status || '').toLowerCase() === selectedStatus
        const matchesStore = !selectedStore || defaultStoreValue === selectedStore

        return matchesSearch && matchesRole && matchesStatus && matchesStore
      })

      this.filteredData = filtered
      this.currentPage = 1
    },
    async loadStores() {
      this.loadingStores = true
      try {
        const result = await getAllStores()
        if (result.success) {
          this.stores = result.data || []
        } else {
          console.error('Error loading stores:', result.error)
          this.stores = []
        }
      } catch (error) {
        console.error('Error loading stores:', error)
        this.stores = []
      } finally {
        this.loadingStores = false
        this.applyFilters()
      }
    },
    getStoreName(defaultStore) {
      if (!defaultStore) return null

      // Convertir defaultStore a string para comparación
      const storeDivision = String(defaultStore)

      // Buscar la tienda que coincida con la división
      const store = this.stores.find(
        s => String(s.division) === storeDivision || String(s.id) === storeDivision
      )

      return store ? store.sucursal : null
    },
    num_pages() {
      return Math.ceil(this.filteredData.length / this.elementsPerPage)
    },
    change_page(page) {
      this.currentPage = page
    },
    change() {
      if (this.currentPage < Math.ceil(this.filteredData.length / this.elementsPerPage)) {
        this.currentPage++
      }
    },
    prev() {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },
    getStatusBadgeClass(status) {
      if (!status) return 'badge-secondary'

      const statusLower = String(status).toLowerCase()
      if (statusLower === 'activo') {
        return 'badge-success'
      } else if (statusLower === 'inactivo') {
        return 'badge-danger'
      } else {
        return 'badge-warning'
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return 'N/A'
      try {
        const date = new Date(Number(timestamp))
        const day = String(date.getDate()).padStart(2, '0')
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const year = date.getFullYear()
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${day}/${month}/${year} ${hours}:${minutes}`
      } catch {
        return 'N/A'
      }
    },
    formatActor(actor) {
      return formatUserActor(actor)
    },
    async copyUserId(uid) {
      try {
        // Usar la API moderna de Clipboard si está disponible
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(uid)
          // Mostrar toast de información
          toast.info('ID de usuario copiado al portapapeles', {
            hideProgressBar: true,
            autoClose: 2000,
            theme: 'colored',
          })
        } else {
          // Fallback para navegadores que no soportan clipboard API
          const textArea = document.createElement('textarea')
          textArea.value = uid
          textArea.style.position = 'fixed'
          textArea.style.left = '-999999px'
          textArea.style.top = '-999999px'
          document.body.appendChild(textArea)
          textArea.focus()
          textArea.select()

          try {
            document.execCommand('copy')
            // Mostrar toast de información
            toast.info('ID de usuario copiado al portapapeles', {
              hideProgressBar: true,
              autoClose: 2000,
              theme: 'colored',
            })
          } catch (err) {
            console.error('Error al copiar ID:', err)
            toast.error('Error al copiar el ID de usuario', {
              hideProgressBar: true,
              autoClose: 2000,
              theme: 'colored',
            })
          }

          document.body.removeChild(textArea)
        }
      } catch (error) {
        console.error('Error al copiar ID:', error)
        toast.error('Error al copiar el ID de usuario', {
          hideProgressBar: true,
          autoClose: 2000,
          theme: 'colored',
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.badge {
  padding: 0.35em 0.65em;
  font-size: 0.875em;
}

.badge-primary {
  background-color: #5c61f2;
  color: white;
}

.badge-success {
  background-color: #28a745;
  color: white;
}

.badge-danger {
  background-color: #dc3545;
  color: white;
}

.badge-warning {
  background-color: #ffc107;
  color: #212529;
}

.badge-secondary {
  background-color: #6c757d;
  color: white;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}

.action-buttons {
  display: flex;
  gap: 0.25rem;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  line-height: 1.5;
  border-radius: 0.2rem;
}

.btn-primary {
  background-color: #5c61f2;
  border-color: #5c61f2;
  color: white;
}

.btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
  color: white;
}

.btn-warning {
  background-color: #ffc107;
  border-color: #ffc107;
  color: #212529;
}

.btn-success {
  background-color: #28a745;
  border-color: #28a745;
  color: white;
}

.btn-info {
  background-color: #17a2b8;
  border-color: #17a2b8;
  color: white;
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
  color: white;
}

.btn:hover {
  opacity: 0.8;
}

/* Estilos para cards de usuarios */
.user-card {
  border: 1px solid #dee2e6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s ease;
}

.user-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.user-card .card-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #212529;
}

.user-info {
  border-top: 1px solid #e9ecef;
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 0;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 0.9rem;
  color: #212529;
  word-break: break-word;
}

.card-actions {
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
  margin-top: 1rem;
}

/* Responsive: ocultar texto en botones en móviles muy pequeños */
@media (max-width: 575.98px) {
  .card-actions .btn span {
    display: none;
  }

  .card-actions .btn {
    min-width: 40px;
    justify-content: center;
  }
}

/* Ajustes para tablets */
@media (min-width: 768px) and (max-width: 991.98px) {
  .card-actions .btn span {
    display: inline;
  }
}
</style>
